import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisVertical, faEnvelope, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { Button, Modal } from 'ui'
import { useGetPartner } from '../../hooks/useGetPartner'
import PartnerHistory from './components/PartnerHistory'
import Notes from './components/Notes'
import Card from '../common/Card'
import CardItem from './components/common/CardItem'
import { DateTime } from 'luxon'
import { useMutation } from '@tanstack/react-query'
import { sendConfirmation, chnageStatusPartner, addProfilePMS } from '../../api/profile'
import { toast } from 'react-toastify'
import PartnerTemplate from './components/PartnerTemplate'
import { Menu, MenuButton, MenuDivider, MenuItem } from '@szhsin/react-menu'
import useGetPartnerHistory from '../../hooks/useGetPartnerHistory'
import { useAppSelector } from '../../store/hooks'
import ProfilesPMSForm from './components/forms/ProfilesPMSForm'
import useGetPartnerPMSProfiles from '../../hooks/useGetPartnerPMSProfiles/index'
import { partnerProfilePMS } from '../../api/partner'
type Props = {}

const Partner = (props: Props) => {
   const intl = useIntl()
   const { id, accountCode } = useParams()
   const navigate = useNavigate()
   const { data: partner, status: partnerStatus, refetch } = useGetPartner(id)
   const { refetch: refetchHistory } = useGetPartnerHistory({ memberId: id, size: 10, page: 0 })
   const { data: acountProfile } = useAppSelector((state) => state.profile)
   const [isOpen, setIsOpen] = useState(false)
   const [isOpenProfile, setIsOpenProfile] = useState(false)
   const [isOpenDeactivateModal, setIsOpenDeactivateModal] = useState(false)
   const [isAgree, setIsAgree] = useState(false)
   const [reason, setReason] = useState('')
   const { mutate } = useMutation({
      mutationFn: sendConfirmation,
   })
   const {
      data: partnerPMSProfiles,
      status: partnerPMSProfilesStatus,
      refetch: refetchPMSProfiles,
   } = useGetPartnerPMSProfiles(id)

   const { mutate: deactivatePartnerFunc, isPending } = useMutation({
      mutationFn: chnageStatusPartner,
   })
   const { mutate: addProfilePMSFunction, isPending: isPendingAddProfile } = useMutation({
      mutationFn: addProfilePMS,
   })
   function sendConfimationEvent() {
      if (id) {
         mutate(id, {
            onSuccess: () => {
               toast.success(intl.formatMessage({ id: 'partner.confirmation.success' }))
               setIsOpen(false)
               refetch()
            },
            onError: (error: any) => {
               toast.error(error?.response?.data?.error || intl.formatMessage({ id: 'partner.confirmation.fail' }))
            },
         })
      }
   }
   function sendDeactivateEvent() {
      if (id && partner) {
         deactivatePartnerFunc(
            { memberId: id, note: reason, externalId: partner.externalId },
            {
               onSuccess: () => {
                  toast.success(
                     intl.formatMessage({ id: `partner.${partner?.isActive ? 'deactivate' : 'activate'}.success` }),
                  )
                  setIsOpenDeactivateModal(false)
                  setIsAgree(false)
                  setReason('')
                  refetch()
                  refetchHistory()
               },
               onError: () => {
                  toast.error(intl.formatMessage({ id: 'partner.status.fail' }))
                  setIsOpenDeactivateModal(false)
                  setIsAgree(false)
                  setReason('')
               },
            },
         )
      }
   }

   const cancelModalStatus = () => {
      setIsOpenDeactivateModal(false)
      setIsAgree(false)
      setReason('')
      setIsOpenProfile(false)
   }
   const cancelModalPMSProfile = () => {
      setIsOpenProfile(false)
      refetch()
      refetchHistory()
   }
   function addProfilePMSSubmit(data: any) {
      addProfilePMSFunction(
         { id: id || '', data },
         {
            onSuccess: () => {
               toast.success(intl.formatMessage({ id: 'partner.profile.add.success' }))
               refetchPMSProfiles()
            },
            onError: (error: any) => {
               toast.error(error?.response?.data?.error || intl.formatMessage({ id: 'partner.profile.add.fail' }))
            },
         },
      )
   }
   const isDisabled = (partner?.isActive && !isAgree) || (!partner?.isActive && reason.length === 0) || isPending
   const isAdmin = acountProfile?.roles.some((role) => role.code === 'LOYALTY_ADMIN')
   const showCompanyField = partner?.loyaltyPlans?.some((plan) => plan?.memberType === 'BYTU')

   return (
      <>
         <PartnerTemplate
            partner={partner}
            partnerStatus={partnerStatus}
            accountCode={accountCode}
            id={id}
            activeTabCode="info"
         >
            <div className="flex flex-col md:flex-row gap-x-4 mx-4">
               <div className="basis-8/12">
                  <Card>
                     <Card.Title>
                        <div className="flex justify-between gap-x-2  items-center">
                           {intl.formatMessage({ id: 'partner.information' })}
                           <Menu
                              menuButton={
                                 <MenuButton className="w-[30px] h-[30px] rounded-full hover:bg-gray-100">
                                    <FontAwesomeIcon icon={faEllipsisVertical} />
                                 </MenuButton>
                              }
                              transition
                              align="end"
                              className={'text-sm'}
                           >
                              <MenuItem onClick={() => navigate(`/${accountCode}/partners/${id}/edit`)}>
                                 {intl.formatMessage({ id: 'partner.edit' })}
                              </MenuItem>
                              <MenuItem onClick={() => setIsOpen(true)}>
                                 {intl.formatMessage({ id: 'partner.confirmation.send' })}
                              </MenuItem>
                              <MenuItem onClick={() => setIsOpenProfile(true)}>
                                 {intl.formatMessage({ id: 'partner.profile' })}
                              </MenuItem>
                              {partner && isAdmin && (
                                 <>
                                    <MenuDivider />
                                    <MenuItem
                                       className={`${partner.isActive ? 'text-red-500' : ''}`}
                                       onClick={() => setIsOpenDeactivateModal(true)}
                                    >
                                       {intl.formatMessage({
                                          id: `partner.${partner.isActive ? 'deactivate' : 'activate'}.tittle`,
                                       })}
                                    </MenuItem>
                                 </>
                              )}
                           </Menu>
                        </div>
                     </Card.Title>
                     <div className="px-4 pt-3 pb-6 relative">
                        <div className="grid grid-cols-2 gap-4">
                           <CardItem label={intl.formatMessage({ id: 'partner.name' })} value={partner?.firstName} />
                           <CardItem label={intl.formatMessage({ id: 'partner.lastName' })} value={partner?.lastName} />
                           <CardItem label={intl.formatMessage({ id: 'partner.email' })} value={partner?.email} />
                           <CardItem label={intl.formatMessage({ id: 'partner.phone' })} value={partner?.phoneNumber} />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.status' })}
                              {...(partner &&
                                 partner.maritalStatus && {
                                    value: intl.formatMessage({ id: `partner.status.${partner.maritalStatus}` }),
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.birthday' })}
                              value={partner?.dateOfBirth}
                           />
                           {
                              <CardItem
                                 label={intl.formatMessage({ id: 'partner.sex' })}
                                 {...(partner &&
                                    partner?.gender && {
                                       value: intl.formatMessage({ id: `partner.sex.${partner.gender}` }),
                                    })}
                              />
                           }

                           <CardItem
                              label={intl.formatMessage({ id: 'partner.country' })}
                              value={partner?.countryOfResidence}
                           />
                           {showCompanyField && (
                              <>
                                 {' '}
                                 <CardItem
                                    label={intl.formatMessage({ id: 'partner.organization' })}
                                    value={partner?.organization}
                                 />
                                 <CardItem
                                    label={intl.formatMessage({ id: 'partner.roleInOrganization' })}
                                    value={partner?.roleInOrganization}
                                 />
                              </>
                           )}
                           {partnerStatus === 'pending' ? (
                              <div className="absolute inset-0 bg-neutral-100 opacity-25 flex items-center justify-center">
                                 <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                              </div>
                           ) : null}
                        </div>
                     </div>
                  </Card>
                  {partner?.loyaltyPlans && partner?.loyaltyPlans.length > 0 && (
                     <Card>
                        <Card.Title>{intl.formatMessage({ id: 'partner.membership.information' })}</Card.Title>
                        {partner?.loyaltyPlans.map((l, index) => (
                           <React.Fragment key={index}>
                              <h3 className="px-4 pt-4 font-semibold">{l.loyaltyPlanName}</h3>
                              <div
                                 className={`px-4 pt-3 pb-6 relative ${
                                    index < partner.loyaltyPlans.length - 1 ? 'border-b' : ''
                                 }`}
                              >
                                 <div className="grid grid-cols-2 gap-4">
                                    {l.allowPoints && (
                                       <CardItem
                                          label={intl.formatMessage({ id: 'partner.points' })}
                                          value={l.points.toString()}
                                       />
                                    )}
                                    <CardItem
                                       label={intl.formatMessage({ id: 'partner.registrationChannel' })}
                                       value={l?.registrationChannel}
                                    />
                                    <CardItem
                                       label={intl.formatMessage({ id: 'partner.registrationDate' })}
                                       value={DateTime.fromISO(l.registrationDate).toFormat('yyyy-MM-dd')}
                                    />
                                    {l.allowPoints && (
                                       <CardItem label={intl.formatMessage({ id: 'partner.level' })} value={l.level} />
                                    )}
                                    {l.memberType === 'BYTU' && (
                                       <CardItem
                                          label={intl.formatMessage({ id: 'partner.segment' })}
                                          value={l.segment}
                                       />
                                    )}
                                 </div>
                              </div>
                           </React.Fragment>
                        ))}
                     </Card>
                  )}
                  <Card>
                     <Card.Title>{intl.formatMessage({ id: 'partner.title.additional' })}</Card.Title>
                     <div className="px-4 pt-3 pb-6 relative">
                        <div className="grid grid-cols-2 gap-4">
                           {partner?.rfc && (
                              <CardItem label={intl.formatMessage({ id: 'partner.form.rfc' })} value={partner.rfc} />
                           )}

                           {partner?.memberCode && (
                              <CardItem
                                 label={intl.formatMessage({ id: 'partner.form.memberCode' })}
                                 value={partner.memberCode}
                              />
                           )}
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.state' })}
                              {...(partner &&
                                 partner?.state && {
                                    value: partner.state,
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.swimming' })}
                              {...(partner &&
                                 partner?.swimmingSkill && {
                                    value: partner.swimmingSkill ? 'Sí' : 'No',
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.phonenumber' })}
                              {...(partner &&
                                 partner?.phoneNumber && {
                                    value: partner.phoneNumber,
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.typeBlood' })}
                              {...(partner &&
                                 partner?.bloodType && {
                                    value: partner.bloodType,
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.allergies' })}
                              {...(partner &&
                                 partner?.allergies && {
                                    value: partner.allergies,
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.passion' })}
                              {...(partner &&
                                 partner?.passion && {
                                    value: partner.passion,
                                 })}
                           />
                           <CardItem
                              label={intl.formatMessage({ id: 'partner.form.pms.perfils' })}
                              {...(partner?.profiles?.length
                                 ? {
                                      value: (
                                         <ul>
                                            {partner?.profiles.map((profile) => (
                                               <li key={profile.id}>
                                                  {profile.system} : {profile.id}
                                               </li>
                                            ))}
                                         </ul>
                                      ),
                                   }
                                 : {})}
                           />
                        </div>
                     </div>
                  </Card>
               </div>
               <div className="basis-4/12">
                 <PartnerHistory partner={partner} />
                  {id && <Notes memberId={id} />}
               </div>
            </div>
            <Modal visible={isOpen} onClose={() => setIsOpen(false)} size="md">
               <Modal.Header>
                  <h2 className="px-4">{intl.formatMessage({ id: 'partner.confirmation.send' })}</h2>
               </Modal.Header>
               <Modal.Body>
                  {partner && (
                     <div className="px-4">
                        <div className="flex items-center gap-x-2">
                           <FontAwesomeIcon icon={faEnvelope} className="text-neutral-500 text-xl" />
                           <div className="leading-tight">
                              <div className="text-neutral-400">{intl.formatMessage({ id: 'partner.email' })}</div>
                              <div>{partner.email}</div>
                           </div>
                        </div>
                     </div>
                  )}
                  <hr className="my-4" />
                  <div className="p-4">{intl.formatMessage({ id: 'partner.confirmation.information' })}</div>
               </Modal.Body>
               <Modal.Footer>
                  <div className="flex justify-end px-4 gap-x-4">
                     <Button bsStyle="success" onClick={sendConfimationEvent}>
                        {intl.formatMessage({ id: 'partner.confirmation.confirm' })}
                     </Button>
                     <Button onClick={() => setIsOpen(false)}>
                        {intl.formatMessage({ id: 'partner.confirmation.cancel' })}
                     </Button>
                  </div>
               </Modal.Footer>
            </Modal>
            <Modal visible={isOpenDeactivateModal} onClose={cancelModalStatus} size="md">
               <Modal.Header>
                  <h2 className="px-4 text-lg font-bold">
                     {' '}
                     {intl.formatMessage({ id: `partner.${partner?.isActive ? 'deactivate' : 'activate'}.tittle` })}
                  </h2>
               </Modal.Header>
               <Modal.Body className="p-8">
                  <div className=" text-lg text-gray-600">
                     <p>
                        {intl.formatMessage(
                           { id: `partner.${partner?.isActive ? 'deactivate' : 'activate'}.alert1` },
                           {
                              name: (
                                 <span className={isAgree ? 'font-bold' : ''}>{`${
                                    partner?.firstName || '' + partner?.lastName
                                 }`}</span>
                              ),
                              id: <span className={isAgree ? 'font-bold' : ''}>{partner?.externalId}</span>,
                           },
                        )}
                     </p>
                     <br />
                     {partner?.isActive ? (
                        <>
                           <ul className="grid justify-center space-y-2 px-4 pb-4 list-disc ">
                              <li className="text-left rounded">
                                 {intl.formatMessage({ id: `partner.desactivate.list-alert1` })}
                              </li>
                              <li className="text-left rounded">
                                 {intl.formatMessage({ id: `partner.desactivate.list-alert2` })}
                              </li>
                              <li className="text-left rounded">
                                 {intl.formatMessage({ id: `partner.desactivate.list-alert3` })}
                              </li>
                              <li className="text-left rounded">
                                 {intl.formatMessage({ id: `partner.desactivate.list-alert4` })}
                              </li>
                           </ul>
                           <label htmlFor="agreed">
                              <input type="checkbox" id="agreed" onClick={() => setIsAgree(!isAgree)} />{' '}
                              {intl.formatMessage({ id: `partner.desactivate.list-alert5` })}
                           </label>
                        </>
                     ) : (
                        <>
                           <label className="block mb-2 font-bold" htmlFor="reason">
                              {intl.formatMessage({ id: 'partner.reaon.activate' })}
                           </label>
                           <textarea
                              onChange={(e) => {
                                 setIsAgree(true)
                                 setReason(e.target.value?.trim())
                              }}
                              name="reason"
                              id="reason"
                              className="w-full h-24 border border-gray-300 rounded p-2"
                              maxLength={200}
                           />
                           <small>{intl.formatMessage({ id: 'partner.reaon.note' })}</small>
                        </>
                     )}
                  </div>
               </Modal.Body>
               <Modal.Footer>
                  <div className="flex justify-end px-4 gap-x-4">
                     <Button bsStyle="success" onClick={sendDeactivateEvent} disabled={isDisabled}>
                        {intl.formatMessage({
                           id: `partner.confirmation.${partner?.isActive ? 'deactivate' : 'activate'}`,
                        })}
                     </Button>
                     <Button disabled={isPending} onClick={cancelModalStatus}>
                        {intl.formatMessage({ id: 'partner.confirmation.cancel' })}
                     </Button>
                  </div>
               </Modal.Footer>
            </Modal>
            <Modal visible={isOpenProfile} onClose={cancelModalPMSProfile} size="md">
               <Modal.Header>
                  <h2 className="px-4 text-lg font-bold">{intl.formatMessage({ id: `partner.tittle.profilePMS` })}</h2>
               </Modal.Header>
               <Modal.Body>
                  {partnerPMSProfiles?.length && partnerPMSProfilesStatus === 'success' ? (
                     <ul className="mb-4 mx-4">
                        {partnerPMSProfiles.map((profile: partnerProfilePMS) => (
                           <li key={profile.profileSourceId}>
                              {profile.pmsSystem} : {profile.profileSourceId}
                              {profile.subtype ? ` - ${profile.subtype}` : ''}
                           </li>
                        ))}
                     </ul>
                  ) : (
                     <div className="mb-4 mx-4 text-gray-600">
                        {intl.formatMessage({ id: 'partner.profile.noProfiles' })}
                     </div>
                  )}
                  <ProfilesPMSForm
                     onSubmit={addProfilePMSSubmit}
                     isLoadingSubmit={isPendingAddProfile}
                     defautlValues={partnerPMSProfiles || []}
                  />
               </Modal.Body>
            </Modal>
         </PartnerTemplate>
      </>
   )
}

export default Partner
